import { CrownOutlined, DeleteOutlined, LinkOutlined, PlusOutlined, PhoneOutlined } from '@ant-design/icons'
import { Layout } from '@community_dev/pixels'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  notification,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useState, type JSX } from 'react'

import { StatusTag } from './StatusTag'
import { VoiceActionModal } from './VoiceActionModal'

import { putClient } from 'api/clients'
import {
  associateNumber,
  deleteNumber,
  linkCampaignToNumber,
  ProvisioningCampaign,
  ProvisioningNumber,
} from 'api/infobipProvisioning'
import { postDefaultIvr, VoiceOptions, VoiceSetup } from 'api/infobipVoice'
import { VOICE_ACTION_LABELS } from 'constants/infobip-voice'
import { QUERIES } from 'constants/queries'

const { Panel } = Collapse
const { Option } = Select

type MasterPhoneNumberControlProps = {
  canWrite: boolean
  clientId: string
  currentMasterNumber: string | null | undefined
  disabledTooltip: string
  isMasterPhoneNumber: boolean
  phoneNumber: string
}

const MasterPhoneNumberControl = ({
  canWrite,
  clientId,
  currentMasterNumber,
  disabledTooltip,
  isMasterPhoneNumber,
  phoneNumber,
}: MasterPhoneNumberControlProps): JSX.Element | null => {
  const queryClient = useQueryClient()

  const { mutate: setMaster, isLoading: isSettingMaster } = useMutation({
    mutationFn: putClient,
    onSuccess: () => {
      notification.success({
        message: 'Master phone number updated successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.CLIENT, { id: clientId }])
    },
    onError: () => {
      notification.error({
        message: 'Failed to update master phone number',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleSetMaster = () => {
    if (!canWrite) return
    setMaster({
      id: clientId,
      body: { master_phone_number: phoneNumber },
    })
  }

  if (isMasterPhoneNumber) {
    return (
      <Tag color="gold" icon={<CrownOutlined />}>
        Master Phone Number
      </Tag>
    )
  }

  return (
    <Popconfirm
      cancelText="Cancel"
      disabled={!canWrite}
      okText="Set as Master"
      onConfirm={handleSetMaster}
      title={
        currentMasterNumber
          ? `Replace ${currentMasterNumber} with ${phoneNumber} as the master phone number?`
          : `Set ${phoneNumber} as the master phone number?`
      }
    >
      <Tooltip title={!canWrite ? disabledTooltip : ''}>
        <span>
          <Button disabled={!canWrite} icon={<CrownOutlined />} loading={isSettingMaster} size="small" type="primary">
            Set as Master
          </Button>
        </span>
      </Tooltip>
    </Popconfirm>
  )
}

type NumberCardProps = {
  canWrite: boolean
  campaigns: ProvisioningCampaign[]
  clientId: string
  currentMasterNumber: string | null | undefined
  disabledTooltip: string
  number: ProvisioningNumber
  voiceOptions: VoiceOptions | undefined
  voiceSetup: VoiceSetup | undefined
}

const NumberCard = ({
  canWrite,
  campaigns,
  clientId,
  currentMasterNumber,
  disabledTooltip,
  number,
  voiceOptions,
  voiceSetup,
}: NumberCardProps): JSX.Element => {
  const queryClient = useQueryClient()
  const [selectedCampaign, setSelectedCampaign] = useState<string>('')
  const [forceLink, setForceLink] = useState(false)
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)

  // currentMasterNumber may carry a leading "+" while number.number never does, so normalize for the compare.
  const isMasterPhoneNumber = currentMasterNumber?.replace(/^\+/, '') === number.number

  const { mutate: deleteNumberMutation, isLoading: isDeleting } = useMutation({
    mutationFn: deleteNumber,
    onSuccess: () => {
      notification.success({
        message: 'Number deleted successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to delete number',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const { mutate: linkCampaign, isLoading: isLinking } = useMutation({
    mutationFn: linkCampaignToNumber,
    onSuccess: () => {
      notification.success({
        message: 'Link request submitted. This may take up to 2 days in production.',
        placement: 'bottomRight',
        duration: 4,
      })
      setSelectedCampaign('')
      setForceLink(false)
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to link campaign to number',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const { mutate: assignDefault, isLoading: isAssigningDefault } = useMutation({
    mutationFn: postDefaultIvr,
    onSuccess: () => {
      notification.success({
        message: 'Default IVR assigned successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.VOICE_STATUS, clientId])
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to assign default IVR',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const linkedCampaignIds = number.campaigns.map((c) => c.campaignId)
  const availableCampaigns = campaigns.filter((c) => !linkedCampaignIds.includes(c.campaignId))

  const handleLinkCampaign = () => {
    if (!canWrite || !selectedCampaign) return
    linkCampaign({
      clientId,
      number: number.number,
      campaignId: selectedCampaign,
      force: forceLink,
    })
  }

  const handleAssignDefaultIvr = () => {
    if (!canWrite) return
    assignDefault({ clientId, phoneNumber: number.number })
  }

  return (
    <Card
      extra={
        <Space>
          <StatusTag status={number.status} />
          <Popconfirm
            cancelText="Cancel"
            disabled={!canWrite}
            okText="Delete"
            onConfirm={() => deleteNumberMutation({ clientId, number: number.number })}
            title="Are you sure you want to delete this number?"
          >
            <Tooltip title={!canWrite ? disabledTooltip : ''}>
              <span>
                <Button danger disabled={!canWrite} icon={<DeleteOutlined />} loading={isDeleting} size="small">
                  Delete
                </Button>
              </span>
            </Tooltip>
          </Popconfirm>
        </Space>
      }
      size="small"
      style={{ marginBottom: '12px' }}
      title={
        <Space>
          <span>{number.number}</span>
          <MasterPhoneNumberControl
            canWrite={canWrite}
            clientId={clientId}
            currentMasterNumber={currentMasterNumber}
            disabledTooltip={disabledTooltip}
            isMasterPhoneNumber={isMasterPhoneNumber}
            phoneNumber={number.number}
          />
        </Space>
      }
    >
      <Collapse defaultActiveKey={isMasterPhoneNumber ? ['voice'] : []} ghost>
        <Panel header="Status Checks" key="checks">
          {number.checks.map((check) => (
            <Layout key={check.id} marginBottom="12px">
              <Layout alignItems="center" display="flex" gap="8px" marginBottom="4px">
                <Typography.Text strong>{check.label}</Typography.Text>
                <StatusTag status={check.status} />
              </Layout>
              {check.help.length > 0 && (
                <Typography.Text type="secondary">
                  {check.help.map((helpText, index) => (
                    <div key={index}>{helpText}</div>
                  ))}
                </Typography.Text>
              )}
            </Layout>
          ))}
        </Panel>

        <Panel header={`Linked Campaigns (${number.campaigns.length})`} key="campaigns">
          {number.campaigns.length === 0 ? (
            <Typography.Text type="secondary">No campaigns linked yet</Typography.Text>
          ) : (
            number.campaigns.map((campaign) => (
              <Layout alignItems="center" display="flex" gap="8px" key={campaign.campaignId} marginBottom="8px">
                <Typography.Text code>{campaign.campaignId}</Typography.Text>
                <StatusTag status={campaign.linkStatus} />
              </Layout>
            ))
          )}
        </Panel>

        <Panel
          header={
            voiceSetup?.action
              ? `Voice Setup (${VOICE_ACTION_LABELS[voiceSetup.action] || voiceSetup.action})`
              : 'Voice Setup (not configured)'
          }
          key="voice"
        >
          {voiceSetup?.action ? (
            <Descriptions bordered column={1} size="small" style={{ marginBottom: '12px' }}>
              <Descriptions.Item label="Action">
                {VOICE_ACTION_LABELS[voiceSetup.action] || voiceSetup.action}
              </Descriptions.Item>
              <Descriptions.Item label="Action Payload">
                <Typography.Text code>{voiceSetup.actionPayload}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Description">{voiceSetup.description}</Descriptions.Item>
            </Descriptions>
          ) : (
            <Typography.Paragraph type="secondary">
              No voice setup yet. Assign the default IVR for the standard voicemail + opt-in flow, or customize a
              specific voice action.
            </Typography.Paragraph>
          )}
          <Space>
            <Popconfirm
              cancelText="Cancel"
              disabled={!canWrite}
              okText="Assign"
              onConfirm={handleAssignDefaultIvr}
              title={
                voiceSetup?.action
                  ? `Replace the current voice setup with the default IVR for ${number.number}?`
                  : `Assign the default IVR to ${number.number}?`
              }
            >
              <Tooltip title={!canWrite ? disabledTooltip : ''}>
                <span>
                  <Button
                    disabled={!canWrite}
                    icon={<PhoneOutlined />}
                    loading={isAssigningDefault}
                    size="small"
                    type="primary"
                  >
                    Assign Default IVR
                  </Button>
                </span>
              </Tooltip>
            </Popconfirm>
            <Tooltip title={!canWrite ? disabledTooltip : ''}>
              <span>
                <Button disabled={!canWrite || !voiceOptions} onClick={() => setVoiceModalOpen(true)} size="small">
                  Customize…
                </Button>
              </span>
            </Tooltip>
          </Space>
        </Panel>
      </Collapse>

      {availableCampaigns.length > 0 && (
        <Layout marginTop="12px">
          <Divider orientation="left" plain>
            Link Campaign
          </Divider>
          <Alert
            description="Linking a campaign takes ~5 days and incurs costs."
            message="Warning"
            showIcon
            style={{ marginBottom: '12px' }}
            type="warning"
          />
          <Tooltip title={!canWrite ? disabledTooltip : ''}>
            <span>
              <Layout alignItems="center" display="flex" gap="8px">
                <Select
                  disabled={!canWrite}
                  onChange={setSelectedCampaign}
                  placeholder="Select a campaign"
                  style={{ width: 200 }}
                  value={selectedCampaign || undefined}
                >
                  {availableCampaigns.map((c) => (
                    <Option key={c.campaignId} value={c.campaignId}>
                      {c.campaignId}
                    </Option>
                  ))}
                </Select>
                <Tooltip title={canWrite ? 'Force re-submission even if already pending or complete' : disabledTooltip}>
                  <Checkbox checked={forceLink} disabled={!canWrite} onChange={(e) => setForceLink(e.target.checked)}>
                    Force
                  </Checkbox>
                </Tooltip>
                <Button
                  disabled={!canWrite || !selectedCampaign}
                  icon={<LinkOutlined />}
                  loading={isLinking}
                  onClick={handleLinkCampaign}
                  type="primary"
                >
                  Link
                </Button>
              </Layout>
            </span>
          </Tooltip>
        </Layout>
      )}

      {voiceOptions && (
        <VoiceActionModal
          clientId={clientId}
          currentSetup={voiceSetup}
          onClose={() => setVoiceModalOpen(false)}
          open={voiceModalOpen}
          options={voiceOptions}
          phoneNumber={number.number}
        />
      )}
    </Card>
  )
}

type PhoneNumbersProps = {
  canWrite: boolean
  clientId: string
  campaigns: ProvisioningCampaign[]
  currentMasterNumber: string | null | undefined
  disabledTooltip: string
  numbers: ProvisioningNumber[]
  voiceOptions: VoiceOptions | undefined
  voiceSetups: Record<string, VoiceSetup> | undefined
}

export const PhoneNumbers = ({
  canWrite,
  clientId,
  numbers,
  campaigns,
  currentMasterNumber,
  disabledTooltip,
  voiceOptions,
  voiceSetups,
}: PhoneNumbersProps): JSX.Element => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  const { mutate: addNumber, isLoading: isAddingNumber } = useMutation({
    mutationFn: associateNumber,
    onSuccess: () => {
      notification.success({
        message: 'Number associated successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      form.resetFields()
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
    },
    onError: () => {
      notification.error({
        message: 'Failed to associate number',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleAddNumber = (values: { number: string }) => {
    if (!canWrite) return
    addNumber({ clientId, number: values.number.trim() })
  }

  return (
    <Card title="Phone Numbers">
      <Form form={form} layout="inline" onFinish={handleAddNumber} style={{ marginBottom: '16px' }}>
        <Form.Item
          name="number"
          rules={[
            { required: true, message: 'Please enter a phone number' },
            { pattern: /^\d+$/, message: 'Phone number must contain only digits' },
          ]}
        >
          {canWrite ? (
            <Input placeholder="Enter phone number (e.g., 14178547324)" style={{ width: 280 }} />
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Input disabled placeholder="Enter phone number (e.g., 14178547324)" style={{ width: 280 }} />
              </span>
            </Tooltip>
          )}
        </Form.Item>
        <Form.Item>
          {canWrite ? (
            <Button htmlType="submit" icon={<PlusOutlined />} loading={isAddingNumber} type="primary">
              Add Number
            </Button>
          ) : (
            <Tooltip title={disabledTooltip}>
              <span>
                <Button disabled htmlType="submit" icon={<PlusOutlined />} loading={isAddingNumber} type="primary">
                  Add Number
                </Button>
              </span>
            </Tooltip>
          )}
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">
        {"To purchase a number, use Infobip's dashboard. Go to Channels and Numbers > Buy Number"}
      </Typography.Text>
      <Divider />
      {numbers.length === 0 ? (
        <Empty description="No numbers associated yet" />
      ) : (
        numbers.map((number) => (
          <NumberCard
            campaigns={campaigns}
            canWrite={canWrite}
            clientId={clientId}
            currentMasterNumber={currentMasterNumber}
            disabledTooltip={disabledTooltip}
            key={number.number}
            number={number}
            voiceOptions={voiceOptions}
            voiceSetup={voiceSetups?.[number.number]}
          />
        ))
      )}
    </Card>
  )
}
